<template>
    <div :class="$style.wrapper">
        <div>
            <Header />
            <InfoBanner :class="$style.desktopOnly" />
            <div
                :class="{
                    [$style.jobFilters]: true,
                    [$style.sidePaneFilters]: isJobDescVisible,
                }"
            >
                <div :class="$style.filtersInner">
                    <div :class="$style.searchBar">
                        <FontAwesomeIcon
                            style="padding-left: 5px"
                            :class="$style.icon"
                            :icon="searchIcon"
                        />
                        <span :class="[$style.innerSpan, $style.desktopOnly]" @click="focusInput">
                            <input
                                placeholder="Search by job title or company"
                                type="text"
                                v-model="searchQuery"
                                ref="searchInput"
                                aria-label="Search by job title or company"
                            />
                        </span>
                        <span :class="[$style.innerSpan, $style.mobileOnly]" @click="focusInput">
                            <input
                                placeholder="Search"
                                type="text"
                                v-model="searchQuery"
                                ref="searchInput"
                                aria-label="Search by job title or company"
                            />
                        </span>
                        <FontAwesomeIcon
                            v-if="this.isCustomSearch"
                            :class="{
                                [$style.icon]: true,
                                [$style.disabled]: !isCustomSearch,
                            }"
                            style="padding-left: 5px; display: none"
                            :icon="clearIcon"
                            @click="clearInput"
                        />
                    </div>
                    <div>
                        <Button
                            :class="$style.filtersButton"
                            color="secondary"
                            size="form"
                            @clicked="showJobFilterDrawer"
                        >
                            <template v-slot:buttonText>
                                <span>Filters {{ appliedFilterCount }}</span>
                            </template>
                            <template v-slot:icon>
                                <div>
                                    <FontAwesomeIcon :icon="filterIcon" />
                                </div>
                            </template>
                        </Button>
                    </div>
                </div>
                <div v-if="appliedFilters.length" :class="$style.filterChipRow">
                    Filtering:
                    <JobFilterChipList
                        :class="$style.filterChipList"
                        @chipSelected="filterOptionChipSelected"
                        @chipRemoved="filterOptionChipRemoved"
                        @countryChipRemoved="filterOptionCountryChipRemoved"
                    />
                    <div :class="$style.removeFilters" @click="removeAppliedFilters">
                        Remove all filters
                    </div>
                </div>
            </div>
            <InfoBanner :class="$style.mobileOnly" />
            <JobFilterDrawer
                v-if="isJobFilterDrawerVisible"
                :filters="availableFilters"
                :appliedFilters="appliedFilters"
                :focusedFilterName="focusedFilterName"
                @applyFilters="handleApplyFilters"
                @closeDrawer="hideJobFilterDrawer"
            />
            <main>
                <Toast 
                    :show="isJobExpiredToastVisible"
                    :color="'warning'"
                    :class="$style.jobExpiredToast"
                >
                    <template #content>
                        The page you requested no longer exists. Check out all the 
                        other opportunities from other great organisations here!
                    </template>
                </Toast>
                <div
                    :class="{
                        [$style.jobListArea]: true,
                        [$style.splitJobListArea]: isJobDescVisible,
                    }"
                >
                    <JobList
                        ref="jobList"
                        @openJobDescription="showJobDescription"
                        :class="{
                            [$style.jobList]: true,
                            [$style.halfWidthList]: isJobDescVisible,
                        }"
                        :isJobDescOpen="isJobDescVisible"
                        :currentlyOpenJobId="currentlyOpenJob?.id"
                    />
                    <JobDescriptionPane
                        ref="jobDescriptionPane"
                        v-if="isJobDescVisible"
                        :class="$style.jobDescPane"
                        :job="this.currentlyOpenJob"
                        @close="hideJobDescription"
                    />
                </div>
                <JobDescriptionModal
                    v-if="isJobDescVisible"
                    :class="$style.jobDescModal"
                    :job="this.currentlyOpenJob"
                    @close="hideJobDescription"
                />
            </main>
        </div>
        <Footer />
    </div>
</template>

<script>
import {
    faSearch,
    faTimesCircle,
    faChevronDown,
    faChevronUp,
} from '@fortawesome/pro-light-svg-icons'
import { faFilter } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { Button, Toast } from '@applied/marmot'
import InfoBanner from '@/components/InfoBanner/InfoBanner.vue'
import Header from './Header/Header.vue'
import Footer from './Footer/Footer.vue'
import JobList from './JobList/JobList.vue'
import JobFilterDrawer from './JobFilterDrawer/JobFilterDrawer.vue'
import JobFilterChipList from './JobFilterChipList/JobFilterChipList.vue'
import JobDescriptionPane from './JobDescription/JobDescriptionPane/JobDescriptionPane.vue'
import JobDescriptionModal from './JobDescription/JobDescriptionModal/JobDescriptionModal.vue'

export default {
    name: 'JobBoard',
    components: {
        Header,
        JobList,
        Footer,
        JobFilterDrawer,
        JobFilterChipList,
        JobDescriptionPane,
        JobDescriptionModal,
        Button,
        FontAwesomeIcon,
        InfoBanner,
        Toast,
    },
    data() {
        return {
            isJobFilterDrawerVisible: false,
            focusedFilterName: null,
            isJobDescVisible: false,
            currentlyOpenJob: null,
            isInfoBannerOpen: true,
            isTextVisible: true,
            scrollPosition: 0,
            isJobExpiredToastVisible: false,
        }
    },
    methods: {
        toggleText() {
            this.isTextVisible = !this.isTextVisible
        },
        focusInput() {
            this.$refs.searchInput.focus()
        },
        clearInput() {
            this.$store.commit('updateSearchQuery', '')
        },
        showJobFilterDrawer() {
            this.isJobFilterDrawerVisible = true
        },
        hideJobFilterDrawer() {
            this.isJobFilterDrawerVisible = false
            this.focusedFilterName = null
        },
        handleApplyFilters(appliedFilters) {
            this.$store.commit('setAppliedFilters', appliedFilters)
        },
        filterOptionChipSelected(chipInfo) {
            this.showJobFilterDrawer()
            this.focusedFilterName = chipInfo.filterName
        },
        filterOptionChipRemoved(chipInfo) {
            const filtersWithoutSelectedOption = []

            this.appliedFilters.forEach((filter) => {
                const newFilter = { ...filter }
                let countryToRemove

                // If the last admin area in a country is being removed then the country
                // also needs to be removed
                if (filter.name === 'Locations') {
                    const areOtherAdminAreaOptionsPresentForThisCountry = filter.options.some(
                        (option) =>
                            option.type === 'adminArea' &&
                            option.country === chipInfo.country &&
                            option.id !== chipInfo.optionId,
                    )

                    if (!areOtherAdminAreaOptionsPresentForThisCountry) {
                        countryToRemove = chipInfo.country
                    }
                }

                newFilter.options = filter.options.filter(
                    (option) => option.id !== chipInfo.optionId && option.name !== countryToRemove,
                )

                if (newFilter.options.length) {
                    filtersWithoutSelectedOption.push(newFilter)
                }
            })

            this.handleApplyFilters(filtersWithoutSelectedOption)
        },
        filterOptionCountryChipRemoved(chipInfo) {
            const filtersWithoutSelectedOption = []

            this.appliedFilters.forEach((filter) => {
                const newFilter = { ...filter }

                if (filter.name === 'Locations') {
                    newFilter.options = filter.options.filter(
                        (option) =>
                            option.id !== chipInfo.optionId &&
                            !chipInfo.adminAreaOptions.some(
                                (adminAreaOption) => adminAreaOption.id === option.id,
                            ),
                    )
                }

                if (newFilter.options.length) {
                    filtersWithoutSelectedOption.push(newFilter)
                }
            })

            this.handleApplyFilters(filtersWithoutSelectedOption)
        },
        calculateScrollPosition(job) {
            const jobList = this.$refs.jobList.$el
            const jobElement = jobList.querySelector(`[data-job-id="${job.id}"]`)
            if (jobElement) {
                const arbitraryTopOffset = 100
                return jobElement.offsetTop - jobList.offsetTop - arbitraryTopOffset
            }
            return 0
        },
        showJobDescription(job) {
            const isPanelAlreadyOpen = this.isJobDescVisible
            this.currentlyOpenJob = job
            this.isJobDescVisible = true

            if (isPanelAlreadyOpen) {
                this.$nextTick(() => {
                    setTimeout(() => {
                        const jobDescriptionPane = this.$refs.jobDescriptionPane.$el
                        jobDescriptionPane.scrollTo({ top: 0, behavior: 'smooth' })
                    }, 100)
                })
            } else {
                this.$nextTick(() => {
                    const jobList = this.$refs.jobList.$el
                    jobList.scrollTop = this.calculateScrollPosition(job)

                    const jobDescriptionPane = this.$refs.jobDescriptionPane.$el
                    jobDescriptionPane.scrollIntoView({ behavior: 'smooth' })
                })
            }
        },
        hideJobDescription() {
            this.isJobDescVisible = false

            this.$nextTick(() => {
                this.scrollPosition = this.calculateScrollPosition(this.currentlyOpenJob)
                window.scrollTo(0, this.scrollPosition)
            })
        },
        removeAppliedFilters() {
            this.$store.commit('setAppliedFilters', [])
        },
    },
    computed: {
        searchIcon() {
            return faSearch
        },
        clearIcon() {
            return faTimesCircle
        },
        filterIcon() {
            return faFilter
        },
        isCustomSearch() {
            return this.$store.state.searchQuery
        },
        availableFilters() {
            return this.$store.state.availableFilters
        },
        appliedFilters() {
            return this.$store.state.appliedFilters
        },
        searchQuery: {
            // Filtering at front end? Crazy right. While we have < 1000 roles this is performant but we should switch this over to being at backend + on ES one day
            get() {
                return this.$store.state.searchQuery
            },
            set(value) {
                this.$store.commit('updateSearchQuery', event.target.value)
            },
        },
        appliedFilterCount() {
            return this.appliedFilters?.length ? `(${this.appliedFilters.length})` : ''
        },
    },
    mounted() {
        const searchParams = new URLSearchParams(window.location.search)
        if (searchParams.get('isExpiredRole') === 'true') {
            setTimeout(() => {
                this.isJobExpiredToastVisible = true
            }, 1000)
            setTimeout(() => {
                this.isJobExpiredToastVisible = false
            }, 7000)
        }
    }
}
</script>

<style module src="./JobBoard.css" />
